import { type ButtonHTMLAttributes } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const BASE =
  'inline-flex items-center justify-center rounded-ui-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-navy disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-amber hover:bg-amber-hover text-navy font-semibold',
  secondary: 'bg-navy-light hover:bg-navy-lighter text-white',
  outline: 'border border-amber text-amber hover:bg-amber hover:bg-opacity-10',
  ghost: 'text-gray-400 hover:text-white hover:bg-navy-light',
  // The rose confirm used by ConfirmDialog — promoted from a className
  // override into a real variant so the danger action is one token away.
  danger: 'bg-rose-600 hover:bg-rose-500 text-white font-semibold',
};

const SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${
        fullWidth ? 'w-full' : ''
      } ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
